import React, { createContext, useContext, useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { jwtDecode } from "jwt-decode";
import { GET_CLIENT, GET_CLIENT_BY_PHONE, GET_MY_CLIENT, type JWTDecoded } from "../pages/authentication";

interface AuthContextType {
  isAuthenticated: boolean;
  user: any | null; // Replace 'any' with your Client type
  loading: boolean;
  logout: () => void;
  refreshAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userPhone, setUserPhone] = useState<string | null>(null);

  // Self-service: the backend's `myClient` query derives the current user
  // from the JWT and has no ADMIN gate. Preferred over the email/phone
  // fallbacks below — those stay as legacy paths for JWTs that lack a
  // resolvable user id.
  const { data: myClientData } = useQuery(GET_MY_CLIENT, {
    skip: !isAuthenticated,
    onCompleted: (data) => {
      if (data.myClient) {
        console.log("AuthContext - Client data fetched via myClient:", data.myClient.email);
        setUser(data.myClient);
        setLoading(false);
      }
    },
    onError: (error) => {
      console.error("AuthContext - myClient error:", error);
      setLoading(false);
      // Fall back to email/phone below
    },
  });

  // Query for client data when we have an email (fallback)
  const { data: clientEmailData } = useQuery(GET_CLIENT, {
    variables: { email: userEmail },
    skip: !userEmail,
    onCompleted: (data) => {
      console.log("Client data fetched by email:", data.clientByEmail);
      setUser(data.clientByEmail);
      setLoading(false);
    },
  });

  // Query for client data when we have a phone number
  const { data: clientPhoneData } = useQuery(GET_CLIENT_BY_PHONE, {
    variables: { phoneNumber: userPhone },
    skip: !userPhone,
    onCompleted: (data) => {
      console.log("Client data fetched by phone:", data.clientByPhone);
      setUser(data.clientByPhone);
      setLoading(false);
    },
  });

  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (token) {
      try {
        const decoded = jwtDecode<JWTDecoded>(token);
        setIsAuthenticated(true);
        
        if (decoded.email) {
          setUserEmail(decoded.email);
          console.log("JWT decoded, email:", decoded.email);
        } else if (decoded.phoneNumber) {
          setUserPhone(decoded.phoneNumber);
          console.log("JWT decoded, phone:", decoded.phoneNumber);
        }
      } catch (error) {
        console.error("Invalid token:", error);
        logout();
      }
    } else {
      setLoading(false);
    }
  }, []);

  const logout = () => {
    localStorage.removeItem("auth_token");
    setIsAuthenticated(false);
    setUser(null);
    setUserEmail(null);
    setUserPhone(null);
    console.log("User logged out");
  };

  const refreshAuth = () => {
    const token = localStorage.getItem("auth_token");
    if (token) {
      try {
        const decoded = jwtDecode<JWTDecoded>(token);
        setIsAuthenticated(true);
        
        if (decoded.email) {
          setUserEmail(decoded.email);
          console.log("Auth refreshed, email:", decoded.email);
        } else if (decoded.phoneNumber) {
          setUserPhone(decoded.phoneNumber);
          console.log("Auth refreshed, phone:", decoded.phoneNumber);
        }
      } catch (error) {
        console.error("Invalid token during refresh:", error);
        logout();
      }
    } else {
      setLoading(false);
    }
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    logout,
    refreshAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
